import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

// V5.4 §7 — employee acknowledgment inbox (basic audited ack, NOT an e-signature)
export const AckInbox = () => {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [items, setItems] = useState(null);
  const load = () => api.get('/me/acknowledgments').then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items || items.length === 0) return null;
  const open = async (a) => {
    try {
      const r = await api.get(`/me/acknowledgments/${a.id}/download`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirm = async (a) => {
    try {
      await api.post(`/me/acknowledgments/${a.id}/confirm`);
      toast.success(L('Acuse registrado', 'Acknowledgment recorded'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-5 shadow-card" data-testid="ack-inbox-card">
      <p className="text-base font-semibold mb-1">{L('Documentos por confirmar', 'Documents to acknowledge')}</p>
      <p className="text-xs text-muted-foreground mb-3">
        {L('Acuse auditado de recepción/lectura. No constituye una firma electrónica con validez legal.',
           'Audited receipt/reading acknowledgment. This is not a legally qualified electronic signature.')}
      </p>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm flex-wrap" data-testid={`ack-item-${a.id}`}>
            <FileText className="h-4 w-4 text-slate-500" />
            <span className="font-medium flex-1 min-w-0 truncate">{a.filename} · v{a.version}</span>
            {a.status === 'acknowledged' ? (
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`ack-done-${a.id}`}>
                {L('Confirmado', 'Acknowledged')} {String(a.acknowledged_at).slice(0, 10)}
              </Badge>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => open(a)} data-testid={`ack-open-${a.id}`}>
                  {L('Ver documento', 'View document')}
                </Button>
                <Button size="sm" onClick={() => confirm(a)} data-testid={`ack-confirm-${a.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{L('Confirmo recepción', 'I confirm receipt')}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
